import { isAddress, type Address, type Chain } from "viem";

import { getTokenAddress } from "./registry";

export type CrossChainSettlementLayer = "SAME_CHAIN" | "ECO" | "ACROSS";

export interface CrossChainPermit {
  /** Allowed source legs: chain + token, with an optional amount cap. */
  from: Array<{ chain: Chain; token: Address; maxAmount?: bigint }>;
  /** Allowed destination legs: chain + token, with an optional recipient pin. */
  to: Array<{ chain: Chain; token: Address; recipient?: Address | "any" }>;
  /** Upper bound on the Permit2 deadline, expressed as Unix seconds. */
  validUntil?: bigint;
  /** Lower bound on the Permit2 deadline, expressed as Unix seconds. */
  validAfter?: bigint;
  /** Per-destination fill-deadline windows, expressed as Unix seconds. */
  fillDeadline?: Array<{ chain: Chain; min?: bigint; max?: bigint }>;
  /** When true, the destination recipient must be the smart account. */
  recipientIsAccount?: boolean;
  /** Allowed settlement layers. Omit or pass an empty array to allow any supported layer. */
  settlementLayers?: CrossChainSettlementLayer[];
}

interface FromLeg {
  chain: Chain;
  token: Address | string;
  maxAmount?: bigint;
}

interface ToLeg {
  chain: Chain;
  token: Address | string;
  recipient?: Address | "any";
}

export interface CreateCrossChainPermissionInput {
  /** Source chain + token, with an optional amount cap. Pass an array for multi-leg permits. */
  from: FromLeg | FromLeg[];
  /** Destination chain + token, with an optional recipient pin. Pass an array for fan-out destinations. */
  to: ToLeg | ToLeg[];
  /** Upper bound on the Permit2 deadline. Accepts Unix-seconds bigint or Date. */
  validUntil?: bigint | Date;
  /** Lower bound on the Permit2 deadline. Accepts Unix-seconds bigint or Date. */
  validAfter?: bigint | Date;
  /** Per-destination fill-deadline windows, expressed as Unix seconds. */
  fillDeadline?: Array<{ chain: Chain; min?: bigint; max?: bigint }>;
  /**
   * Allow the destination recipient to differ from the smart account.
   * Defaults to false so bridge permissions are bridge-to-self unless the
   * caller explicitly broadens the grant.
   */
  allowRecipientNotAccount?: boolean;
  /** Settlement layers this session is permitted to use. */
  settlementLayers?: CrossChainSettlementLayer[];
}

function toUnixSecondsBigint(input: bigint | Date): bigint {
  if (typeof input === "bigint") return input;
  // Permit2 deadlines are Unix seconds; Date stores milliseconds.
  return BigInt(Math.floor(input.getTime() / 1000));
}

function resolveTokenForChain(token: Address | string, chainId: number): Address {
  return isAddress(token, { strict: false }) ? token : getTokenAddress(token, chainId);
}

/**
 * Build a CrossChainPermit for Permit2-backed bridge settlement.
 *
 * The returned object is JSON-safe after callers stringify bigint fields at
 * the iframe/API boundary and can be passed as
 * `grantPermissions({ crossChainPermits: [...] })`.
 */
export function createCrossChainPermission(
  input: CreateCrossChainPermissionInput,
): CrossChainPermit {
  const fromLegs = Array.isArray(input.from) ? input.from : [input.from];
  const toLegs = Array.isArray(input.to) ? input.to : [input.to];

  if (fromLegs.length === 0) {
    throw new Error("createCrossChainPermission: `from` must contain at least one leg");
  }
  if (toLegs.length === 0) {
    throw new Error("createCrossChainPermission: `to` must contain at least one leg");
  }

  const validUntil =
    input.validUntil !== undefined ? toUnixSecondsBigint(input.validUntil) : undefined;
  const validAfter =
    input.validAfter !== undefined ? toUnixSecondsBigint(input.validAfter) : undefined;

  // Catch a broken time window before the user reaches the passkey dialog.
  if (validUntil !== undefined && validAfter !== undefined && validAfter > validUntil) {
    throw new Error(
      `createCrossChainPermission: validAfter (${validAfter}) is greater than validUntil (${validUntil})`,
    );
  }

  return {
    from: fromLegs.map((leg) => ({
      chain: leg.chain,
      token: resolveTokenForChain(leg.token, leg.chain.id),
      maxAmount: leg.maxAmount,
    })),
    to: toLegs.map((leg) => ({
      chain: leg.chain,
      token: resolveTokenForChain(leg.token, leg.chain.id),
      recipient: leg.recipient,
    })),
    validUntil,
    validAfter,
    fillDeadline: input.fillDeadline,
    recipientIsAccount: !input.allowRecipientNotAccount,
    settlementLayers: input.settlementLayers,
  };
}
